import { useNavigate } from 'react-router-dom';
import { MODELS, useAnalyticsState } from '@/analytics/lib';
import { useDialogs } from './dialogs';
import { Icon, PlatformIcon } from './icons';
import { Badge, Btn, Divider, Label, PageHeading, Panel, PanelBody, PanelHead, SelectWrap, SettingsNote, TextBtn, money, ratio } from './ui';
import { ReportToolbar } from './ReportToolbar';
import { DateDialog, DateFooter, SaveReportDialog } from './reportDialogs';
import { modelLabel, rangeText, useChannels } from './data';

const DESCRIPTIONS = {
  last: 'The last tracked ad touch before a conversion receives all of its credit.',
  first: 'The first tracked ad touch in the window receives all of the credit.',
  linear: 'Every tracked ad touch inside the window receives an equal share of the credit.',
  position: '40% to the first touch, 40% to the last, and 20% shared across the middle.',
};
const SHARES = { last: ['0', '0', '100'], first: ['100', '0', '0'], linear: ['33.3', '33.3', '33.3'], position: ['40', '20', '40'] };

// Attribution — the prototype's model comparison: the same revenue, re-credited by channel under two models.
export default function AttributionPage() {
  const [state, update] = useAnalyticsState();
  const navigate = useNavigate();
  const { openDialog } = useDialogs();
  const compareModel = MODELS.some(m => m.value === state.params.get('cm')) ? state.params.get('cm') : (state.model === 'last' ? 'first' : 'last');
  const a = useChannels(state, state.model);
  const b = useChannels(state, compareModel);
  const bBy = new Map(b.rows.map(r => [r.id, r]));
  const rows = a.rows.map(r => ({ ...r, other: bBy.get(r.id) || { revenue: 0, roas: null } }));
  const total = a.total;
  const shares = SHARES[state.model] || SHARES.linear;
  const keep = () => { const sp = new URLSearchParams(state.params); sp.delete('contact'); sp.delete('contactTab'); sp.delete('cm'); return sp.toString(); };

  return <>
    <PageHeading eyebrow="ATTRIBUTION" title="See the whole story" description="Compare the models. Understand the role of every channel."
      actions={<>
        <Btn icon="calendar" onClick={() => openDialog({ title: 'Reporting period', body: <DateDialog state={state} update={update} />, footer: <DateFooter /> })}>{rangeText(state)}</Btn>
        <Btn primary icon="save" onClick={() => openDialog({ title: 'Save your perspective', body: <SaveReportDialog state={state} page="attribution" defaultName="Attribution model comparison" /> })}>Save comparison</Btn>
      </>} />
    <ReportToolbar state={state} update={update} page="attribution" showModel={false} showSave={false} />
    <div className="two-columns">
      <Panel>
        <PanelHead title="Attribution model comparison" description="Same revenue. A different perspective."><Badge tone="blue">{money(total)} total</Badge></PanelHead>
        <div className="attribution-header">
          <SelectWrap label="Attribution model" value={state.model} options={MODELS} onChange={v => update({ model: v === 'last' ? null : v })} />
          <Icon name="compare" />
          <SelectWrap label="Comparison model" value={compareModel} options={MODELS} onChange={v => update({ cm: v })} />
          <p className="model-description">Credit changes. Your actual ad spend and total revenue stay the same.</p>
        </div>
        <div className="table-scroll">
          <table className="data-table compare-table">
            <thead><tr><th>Channel</th><th className="num">{modelLabel(state.model)}</th><th className="num">{modelLabel(compareModel)}</th><th className="num">Difference</th></tr></thead>
            <tbody>{a.query.isLoading ? <tr><td colSpan={4} className="muted">Loading…</td></tr> : rows.length ? rows.map(c => {
              const d = c.revenue - c.other.revenue;
              const name = c.channel;
              return <tr key={c.id} onClick={() => navigate(`/campaigns?${keep()}&source=${encodeURIComponent(c.raw)}`)} style={{ cursor: 'pointer' }}>
                <td><div className="campaign-cell"><PlatformIcon name={c.raw === '(none)' ? 'direct' : c.raw} /><button type="button" className="text-btn" onClick={e => { e.stopPropagation(); navigate(`/campaigns?${keep()}&source=${encodeURIComponent(c.raw)}`); }}>{name}</button></div></td>
                <td className="num">{money(c.revenue)}<div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 6 }}>{ratio(c.roas)} ROAS</div></td>
                <td className="num">{money(c.other.revenue)}<div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 6 }}>{ratio(c.other.roas)} ROAS</div></td>
                <td className={`num ${d >= 0 ? 'positive' : 'negative'}`}>{d >= 0 ? '+' : '−'}{money(Math.abs(d))}</td>
              </tr>; }) : <tr><td colSpan={4}><div className="empty-state" style={{ padding: 30 }}><p>No attributed revenue in this period.</p></div></td></tr>}
            </tbody>
          </table>
        </div>
        <SettingsNote>Credit is compared against {modelLabel(compareModel).toLowerCase()}. Positive differences show more revenue credited by {modelLabel(state.model).toLowerCase()}.</SettingsNote>
      </Panel>
      <aside className="learning-card">
        <Label blue>HOW IT WORKS</Label>
        <h3>One journey.<br />More than one perspective.</h3>
        <p>{DESCRIPTIONS[state.model]}</p>
        <div className="touch-example">{shares.map((s, i) => (
          <div key={i}><span className="touch-stage"><span className="type-circle" aria-hidden="true">{i + 1}</span><span>{i + 1}<span className="type-sup">{['st', 'nd', 'rd'][i]}</span> touch</span></span><strong>{s}%</strong></div>))}</div>
        {state.model === 'linear' && <p className="model-math type-caption">Three eligible touches. <span className="type-fraction" aria-label="one third">1/3</span> of the credit each.</p>}
        <p>Only tracked ad touches inside the {state.window === '0' ? 'lookback' : `${state.window}-day`} window are eligible. Direct visits and email remain visible in the customer timeline.</p>
        <Divider />
        <TextBtn icon="arrow" onClick={() => navigate('/journeys')}>Explore customer journeys</TextBtn>
      </aside>
    </div>
    <Panel>
      <PanelHead title="What changes when the model changes?" description="A lens on contribution, not a claim of causality." />
      <PanelBody>
        <div className="three-columns">
          {[['target', 'Discovery', 'First touch surfaces the campaigns that introduce new customers to your brand.'], ['journey', 'Consideration', 'Linear attribution gives every tracked ad touch a share of the outcome.'], ['bag', 'Conversion', 'Last touch identifies the ad interaction immediately preceding a purchase.']].map(([ic, title, body]) => (
            <div key={title} style={{ padding: '12px 8px' }}><Icon name={ic} /><h3 style={{ margin: '12px 0 8px' }}>{title}</h3><p className="muted" style={{ fontSize: 12, lineHeight: 1.9 }}>{body}</p></div>))}
        </div>
      </PanelBody>
    </Panel>
  </>;
}
